import { ChangeDetectionStrategy, Component, inject, signal, ViewEncapsulation } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { RouterLink } from '@angular/router';
import { AtIconComponent } from '@anothertrip/anothertrip-ds';

import { MapJob, MapJobsService } from '../../services/map-jobs.service';
import { isMapInProgress, mapViewState, MapViewState } from '../../utils/map-status.util';

type NoticeKind = Exclude<MapViewState, 'idle' | 'generating'>;

interface NoticeCopy {
  title: string;
  action: string;
  icon: string;
}

interface MapNotice extends NoticeCopy {
  id: number;
  kind: NoticeKind;
  job: MapJob;
}

/** The most notices on screen at once; a newer one pushes the oldest out. */
export const MAX_MAP_NOTICES = 4;

const NOTICE_COPY: Record<NoticeKind, NoticeCopy> = {
  ready: { title: 'Your map is ready', action: 'View map', icon: 'check_circle' },
  'non-travel': { title: 'No travel content found in this post', action: 'Try again', icon: 'info' },
  empty: { title: 'No places found in this post', action: 'Try again', icon: 'info' },
  failed: { title: "We couldn't generate this map", action: 'Retry', icon: 'error' },
  // Instagram's answer about the post, not our failure. Trying again gets the same answer until the post changes on
  // Instagram, so the notice links to the post rather than offering a retry (AT-2692).
  unavailable: { title: "We couldn't open this post on Instagram", action: 'View post', icon: 'info' },
  // The daily cap of map regenerations (AT-2743): a retry today would be refused again, so there is no action.
  limit: { title: "You've used today's map regenerations. Try again tomorrow.", action: '', icon: 'info' },
};

/** A final status the map control doesn't know reads as a failure here: there is no map to open. */
const noticeKind = ({ status, requestId, failureReason }: MapJob): NoticeKind => {
  const state: MapViewState = mapViewState(status, requestId, failureReason);
  return state === 'idle' || state === 'generating' ? 'failed' : state;
};

/**
 * Says when a map finishes generating, wherever the creator is in the app, with a link to the map or a way to try
 * again (AT-2685). It sits in the app shell; `MapJobsService` is root-scoped, so a map started on the posts page is
 * still followed after the creator moves on. A notice stays until it is dismissed or acted on.
 */
@Component({
  selector: 'app-map-job-notices',
  imports: [AtIconComponent, RouterLink],
  templateUrl: './map-job-notices.component.html',
  styleUrl: './map-job-notices.component.scss',
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MapJobNoticesComponent {
  private readonly jobs = inject(MapJobsService);

  protected readonly notices = signal<MapNotice[]>([]);
  private nextId = 0;

  constructor() {
    this.jobs.finished$.pipe(takeUntilDestroyed()).subscribe((job) => this.add(job));

    // A post generating again makes its last notice stale, wherever the retry came from.
    this.jobs.changes$
      .pipe(takeUntilDestroyed())
      .subscribe((job) => isMapInProgress(job.status) && this.dropPost(job.mediaId));
  }

  protected dismiss(id: number): void {
    this.notices.update((list) => list.filter((notice) => notice.id !== id));
  }

  protected onRetry(notice: MapNotice): void {
    this.dismiss(notice.id);
    this.jobs.retry(notice.job, notice.job.requestId);
  }

  private dropPost(mediaId: string): void {
    this.notices.update((list) => list.filter((notice) => notice.job.mediaId !== mediaId));
  }

  private add(job: MapJob): void {
    const kind: NoticeKind = noticeKind(job);
    const notice: MapNotice = { id: this.nextId++, kind, job, ...NOTICE_COPY[kind] };

    // One notice per post: a newer result for the same post replaces the older one.
    this.notices.update((list) =>
      [...list.filter((item) => item.job.mediaId !== job.mediaId), notice].slice(-MAX_MAP_NOTICES)
    );
  }
}
